import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Textarea } from '../ui/Input';
import { Alert } from '../ui/Alert';
import useTelemetry from '../../hooks/useTelemetry';

const CATEGORIES = [
  { value: 'bug', label: 'Bug Report', description: 'Something isn\'t working correctly' },
  { value: 'feature_request', label: 'Feature Request', description: 'I\'d like to see a new feature' },
  { value: 'ux_issue', label: 'UX Issue', description: 'Something is confusing or hard to use' },
  { value: 'general', label: 'General Feedback', description: 'Other feedback or suggestions' },
];

export default function FeedbackPage() {
  const [category, setCategory] = useState('');
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { emit } = useTelemetry();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || !feedback.trim()) {
      setError('Please select a category and provide your feedback.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      // Submit feedback via telemetry
      emit('feedback.submitted', { category, feedback: feedback.trim() });
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-lg mx-auto text-center py-12">
        <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Thank you!</h2>
        <p className="text-gray-600 mb-6">Your feedback has been submitted. We appreciate you taking the time to help us improve.</p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="px-6 max-w-lg mx-auto">
      <div className="mb-6">
        <h1 style={{ font: 'var(--text-h1)', color: 'var(--color-text-primary)', margin: 0 }}>Submit Feedback</h1>
        <p className="mt-1 text-sm text-gray-600">
          Help us improve BIDIT by sharing your thoughts, reporting issues, or requesting features.
        </p>
      </div>

      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Category selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Category <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CATEGORIES.map(cat => (
                <label
                  key={cat.value}
                  className={`flex flex-col p-3 rounded-md border border-[color:var(--color-border)] cursor-pointer transition-colors ${
                    category === cat.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-[color:var(--color-border)] hover:border-[color:var(--color-border)]'
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.value}
                    checked={category === cat.value}
                    onChange={(e) => setCategory(e.target.value)}
                    className="sr-only"
                    style={{ accentColor: 'var(--color-action-primary)' }}
                  />
                  <span className="text-sm font-medium text-gray-900">{cat.label}</span>
                  <span className="text-xs text-gray-500 mt-0.5">{cat.description}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Feedback text */}
          <div>
            <label htmlFor="feedback-text" className="block text-sm font-medium text-gray-700 mb-1">
              Your Feedback <span className="text-red-500">*</span>
            </label>
            <Textarea
              id="feedback-text"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={5}
              className="w-full"
              placeholder="Describe your feedback in detail..."
              required
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--color-border)]">
            <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
